import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import InfiniteScroll from 'react-infinite-scroll-component';
import { getCommonLiveTasksPaged, getAllWorkTypes } from '../../../services/api/implementation/impl';
import { Loader2, ChevronDown } from 'lucide-react';
import { SwipeableDrawer } from '@mui/material';
import TaskDetailsPage from '../../TaskDetailsPage';
import TaskManagementEmptyState from './TaskManagementEmptyState';

const PAGE_SIZE = 30;

const formatDate = (dateString) => {
  if (!dateString) return '--';
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear()).slice(-2);
  return `${day}-${month}-${year}`;
};

const formatDateRange = (start, end) => {
  const s = formatDate(start);
  const e = formatDate(end);
  if (s === '--' && e === '--') return '--';
  return (
    <span className="leading-5">
      {s}
      <br />
      to {e}
    </span>
  );
};

const calculateDelay = (task) => {
  if (!task?.planned_end_date) return '--';
  const plannedEnd = new Date(task.planned_end_date);
  const today = new Date();
  let delay;
  if (task?.status === 'To Do' || task?.status === 'In Progress') {
    delay = Math.floor((today - plannedEnd) / (1000 * 60 * 60 * 24));
  } else if (task?.status === 'Completed') {
    if (!task?.actual_end_date) return '--';
    const actualEnd = new Date(task.actual_end_date);
    delay = Math.floor((actualEnd - plannedEnd) / (1000 * 60 * 60 * 24));
  } else {
    return '--';
  }
  return delay > 0 ? `${delay} day${delay > 1 ? 's' : ''}` : '--';
};

const HeaderCell = ({ children }) => (
  <th className="px-4 py-3 text-sm font-medium text-gray-600 bg-gray-100 whitespace-nowrap sticky top-0 z-10 align-middle">
    <span className="inline-flex items-center min-h-[20px] leading-none">{children}</span>
  </th>
);

const STATUS_OPTIONS = [
  { value: 'To Do', label: 'To Do' },
  { value: 'In Progress', label: 'In Progress' },
  { value: 'Completed', label: 'Completed' },
  { value: 'Paused', label: 'Paused' },
  { value: 'Resume', label: 'Resume' },
  { value: 'Review', label: 'Review' }
];

const CommonTaskTable = ({ projectId }) => {
  const [workTypeFilter, setWorkTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const workTypeRef = useRef(null);
  const statusRef = useRef(null);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { data: allWorkTypes } = useQuery({
    queryKey: ['getAllWorkTypes'],
    queryFn: getAllWorkTypes
  });

  const workTypes = allWorkTypes?.filter((wt) => wt.type === 'Common') || [];

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isError,
    error
  } = useInfiniteQuery({
    queryKey: ['getCommonLiveTasksPaged', projectId, workTypeFilter, statusFilter],
    queryFn: ({ pageParam = 1 }) =>
      getCommonLiveTasksPaged({
        projectId,
        page: pageParam,
        limit: PAGE_SIZE,
        workTypeId: workTypeFilter || undefined,
        status: statusFilter || undefined
      }),
    getNextPageParam: (lastPage, allPages) => {
      const meta = lastPage?.meta;
      if (meta?.total != null && meta?.page != null && meta?.limit) {
        const totalPages = Math.ceil(meta.total / meta.limit);
        const next = meta.page + 1;
        return next <= totalPages ? next : undefined;
      }
      const pageData = lastPage?.data || [];
      if (pageData.length < PAGE_SIZE) return undefined;
      return allPages.length + 1;
    },
    enabled: !!projectId,
    initialPageParam: 1
  });

  const flatTasks = useMemo(
    () => data?.pages?.flatMap((p) => p?.data || []) ?? [],
    [data?.pages]
  );

  useEffect(() => {
    const handleClickOutside = (e) => {
      const inWorkType = workTypeRef.current?.contains(e.target);
      const inStatus = statusRef.current?.contains(e.target);
      if (!inWorkType && !inStatus) {
        setDropdownOpen(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedWorkTypeName = workTypes.find((wt) => wt.id === parseInt(workTypeFilter, 10))?.name;
  const hasActiveFilter = workTypeFilter || statusFilter;

  const loadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  const scrollRootId = 'common-task-scroll';

  const renderCommonThead = () => (
    <thead>
      <tr className="border-b border-gray-200">
        <HeaderCell>Task Name</HeaderCell>
        <th
          className="px-4 py-3 text-sm font-medium text-gray-600 bg-gray-100 whitespace-nowrap sticky top-0 z-20 align-middle"
          ref={workTypeRef}>
          <div className="relative inline-flex items-center min-h-[20px]">
            <span
              className="inline-flex items-center gap-1 cursor-pointer leading-none"
              onClick={() => setDropdownOpen(dropdownOpen === 'workType' ? null : 'workType')}>
              {selectedWorkTypeName || 'Work Type'}{' '}
              <ChevronDown size={14} className="text-gray-400" />
            </span>
            {dropdownOpen === 'workType' && (
              <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg min-w-[200px] max-h-60 overflow-y-auto z-50">
                <div
                  className={`px-4 py-2.5 cursor-pointer hover:bg-gray-50 text-sm ${
                    !workTypeFilter ? 'bg-[#2A565E]/10 text-[#2A565E] font-medium' : 'text-gray-700'
                  }`}
                  onClick={() => {
                    setWorkTypeFilter('');
                    setDropdownOpen(null);
                  }}>
                  {workTypeFilter ? 'Clear Filter' : 'All Work Types'}
                </div>
                {workTypes.map((wt) => (
                  <div
                    key={wt.id}
                    className={`px-4 py-2.5 cursor-pointer hover:bg-gray-50 text-sm ${
                      parseInt(workTypeFilter, 10) === wt.id
                        ? 'bg-[#2A565E]/10 text-[#2A565E] font-medium'
                        : 'text-gray-700'
                    }`}
                    onClick={() => {
                      setWorkTypeFilter(String(wt.id));
                      setDropdownOpen(null);
                    }}>
                    {wt.name}
                  </div>
                ))}
              </div>
            )}
          </div>
        </th>
        <HeaderCell>Planned Dates</HeaderCell>
        <HeaderCell>Actual Dates</HeaderCell>
        <th
          className="px-4 py-3 text-sm font-medium text-gray-600 bg-gray-100 whitespace-nowrap sticky top-0 z-20 align-middle"
          ref={statusRef}>
          <div className="relative inline-flex items-center min-h-[20px]">
            <span
              className="inline-flex items-center gap-1 cursor-pointer leading-none"
              onClick={() => setDropdownOpen(dropdownOpen === 'status' ? null : 'status')}>
              {statusFilter || 'Status'} <ChevronDown size={14} className="text-gray-400" />
            </span>
            {dropdownOpen === 'status' && (
              <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg min-w-[180px] max-h-60 overflow-y-auto z-50">
                <div
                  className={`px-4 py-2.5 cursor-pointer hover:bg-gray-50 text-sm ${
                    !statusFilter ? 'bg-[#2A565E]/10 text-[#2A565E] font-medium' : 'text-gray-700'
                  }`}
                  onClick={() => {
                    setStatusFilter('');
                    setDropdownOpen(null);
                  }}>
                  {statusFilter ? 'Clear Filter' : 'All Status'}
                </div>
                {STATUS_OPTIONS.map((opt) => (
                  <div
                    key={opt.value}
                    className={`px-4 py-2.5 cursor-pointer hover:bg-gray-50 text-sm ${
                      statusFilter === opt.value ? 'bg-[#2A565E]/10 text-[#2A565E] font-medium' : 'text-gray-700'
                    }`}
                    onClick={() => {
                      setStatusFilter(opt.value);
                      setDropdownOpen(null);
                    }}>
                    {opt.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </th>
        <HeaderCell>Task Delay</HeaderCell>
      </tr>
    </thead>
  );

  const clearCommonFilters = () => {
    setWorkTypeFilter('');
    setStatusFilter('');
  };

  return (
    <>
      <div
        id={scrollRootId}
        className="overflow-auto overscroll-y-none rounded-lg border border-gray-200 shadow-sm bg-white h-[calc(100vh-10rem)]">
        {isFetching && !data?.pages?.length ? (
          <div className="flex flex-col items-center justify-center h-full gap-3 text-gray-400">
            <Loader2 className="w-8 h-8 animate-spin text-[#2A565E]" />
            <span className="text-base font-medium">Fetching your tasks, please wait...</span>
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center h-full gap-2 px-4 text-center text-red-600 text-sm">
            <span>Could not load common tasks.</span>
            <span className="text-gray-500">{error?.message || 'Please try again.'}</span>
          </div>
        ) : flatTasks.length === 0 ? (
          <table className="w-full text-left text-sm">
            {renderCommonThead()}
            <tbody>
              <tr>
                <td colSpan={6} className="p-0 align-middle bg-white">
                  <TaskManagementEmptyState
                    variant="common"
                    layout="tableCell"
                    liveTasksTab="commonTasks"
                    hasActiveFilter={!!hasActiveFilter}
                    onClearFilters={clearCommonFilters}
                  />
                </td>
              </tr>
            </tbody>
          </table>
        ) : (
          <InfiniteScroll
            dataLength={flatTasks.length}
            next={loadMore}
            hasMore={!!hasNextPage}
            scrollableTarget={scrollRootId}
            style={{ overflow: 'visible' }}
            loader={
              <div className="flex justify-center items-center py-4 gap-2 text-gray-600">
                <Loader2 className="w-6 h-6 animate-spin text-[#2A565E]" />
                <span className="text-sm">Loading more…</span>
              </div>
            }
            endMessage={
              flatTasks.length > 0 ? (
                <div className="flex justify-center py-3 text-xs text-gray-400">End of list</div>
              ) : null
            }>
            <table className="w-full text-left text-sm">
              {renderCommonThead()}
              <tbody>
                {flatTasks.map((task) => (
                  <tr
                    key={task.id}
                    className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => {
                      setSelectedTaskId(task.id);
                      setDrawerOpen(true);
                    }}>
                    <td className="px-4 py-3 text-gray-800 font-medium">{task.name || '--'}</td>
                    <td className="px-4 py-3 text-gray-600">{task.workType?.name || '--'}</td>
                    <td className="px-4 py-3 text-gray-600 text-[13px]">
                      {formatDateRange(task.planned_start_date, task.planned_end_date)}
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-[13px]">
                      {formatDateRange(task.actual_start_date, task.actual_end_date)}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{task.status || '--'}</td>
                    <td className="px-4 py-3 text-red-500 font-medium">{calculateDelay(task)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </InfiniteScroll>
        )}
      </div>
      <SwipeableDrawer
        anchor="right"
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedTaskId(null);
        }}
        onOpen={() => setDrawerOpen(true)}>
        <TaskDetailsPage apiType="common" taskID={selectedTaskId} drawerState={drawerOpen} />
      </SwipeableDrawer>
    </>
  );
};

export default CommonTaskTable;
